import React, { lazy, Suspense, useEffect } from 'react'
import { Route, Switch } from 'react-router-dom'
import { ConnectedRouter } from 'connected-react-router'
import { CSSTransition, SwitchTransition } from 'react-transition-group'
import { connect } from 'react-redux'

import Layout from 'layouts'
import nProgress from 'nprogress'
import Landing from 'pages'

const routes = [
  // Auth Pages
  {
    path: '/account/login',
    Component: lazy(() => import('pages/auth/login')),
    exact: true,
  },
  {
    path: '/account/forgot-password',
    Component: lazy(() => import('pages/auth/forgot-password')),
    exact: true,
  },
  {
    path: '/account/reset-password',
    Component: lazy(() => import('pages/auth/reset-password')),
    exact: true,
  },
  {
    path: '/account/password-reset',
    Component: lazy(() => import('pages/auth/password-reset')),
    exact: true,
  },
  {
    path: '/change-password',
    Component: lazy(() => import('pages/auth/change-password')),
    exact: true,
  },
  // clover
  {
    path: '/clover/register',
    Component: lazy(() => import('pages/clover/register')),
    exact: true,
  },
  // register
  {
    path: '/onboard/register',
    Component: lazy(() => import('pages/register')),
    exact: true,
  },
  {
    path: '/shopify/register',
    Component: lazy(() => import('pages/register/shopify')),
    exact: true,
  },
  // Settings
  {
    path: '/settings/website-widget',
    Component: lazy(() => import('pages/settings/website-widget')),
    exact: true,
  },
  {
    path: '/settings/reward-program',
    Component: lazy(() => import('pages/settings/reward-program/index')),
    exact: true,
  },
  {
    path: '/settings/business-profile',
    Component: lazy(() => import('pages/settings/business')),
    exact: true,
  },
  {
    path: '/settings/brand',
    Component: lazy(() => import('pages/brand')),
    exact: true,
  },
  {
    path: '/settings/reward-card-design',
    Component: lazy(() => import('pages/card-design')),
    exact: true,
  },
  {
    path: '/settings/gift-card-design',
    Component: lazy(() => import('pages/gift-card-design')),
    exact: true,
  },
  {
    path: '/settings/provider-allowance-card-design',
    Component: lazy(() => import('pages/provider-allowance-card-design')),
    exact: true,
  },
  {
    path: '/settings/smart-inbox-setup',
    Component: lazy(() => import('pages/assistant')),
    exact: true,
  },

  // Customers
  {
    path: '/customers',
    Component: lazy(() => import('pages/customers/list')),
    exact: true,
  },

  // Integrations
  {
    path: '/integrations',
    Component: lazy(() => import('pages/partners/list')),
    exact: true,
  },
  {
    path: '/integrations/shopify',
    Component: lazy(() => import('pages/partners/shopify')),
    exact: true,
  },

  // custom rewards
  {
    path: '/custom-rewards',
    Component: lazy(() => import('pages/discounts/list')),
    exact: true,
  },
  {
    path: '/custom-rewards/create',
    Component: lazy(() => import('pages/discounts/create')),
    exact: true,
  },

  // posts
  {
    path: '/marketing',
    Component: lazy(() => import('pages/posts')),
    exact: true,
  },
  {
    path: '/marketing/create',
    Component: lazy(() => import('pages/posts/create')),
    exact: true,
  },
  {
    path: '/marketing/:postId/edit',
    Component: lazy(() => import('pages/posts/edit')),
    exact: true,
  },

  // Reports
  {
    path: '/reports/reward-redeemed',
    Component: lazy(() => import('pages/reports/reward-redeemed')),
    exact: true,
  },
  {
    path: '/reports/gift-issued',
    Component: lazy(() => import('pages/reports/gift-issued')),
    exact: true,
  },
  {
    path: '/reports/gift-redeemed',
    Component: lazy(() => import('pages/reports/gift-redeemed')),
    exact: true,
  },
  {
    path: '/reports/provider-allowance-issued',
    Component: lazy(() => import('pages/reports/provider-allowance-issued')),
    exact: true,
  },
  {
    path: '/reports/provider-allowance-redeemed',
    Component: lazy(() => import('pages/reports/provider-allowance-redeemed')),
    exact: true,
  },
  {
    path: '/reports/communication-history',
    Component: lazy(() => import('pages/reports/communication-history')),
    exact: true,
  },
  {
    path: '/reports/missed-calls',
    Component: lazy(() => import('pages/reports/missed-calls')),
    exact: true,
  },
  {
    path: '/reports/sales-overview',
    Component: lazy(() => import('pages/reports/sales-overview')),
    exact: true,
  },

  // Dashboards
  {
    path: '/dashboard',
    Component: lazy(() => import('pages/dashboard')),
    exact: true,
  },

  // survey
  {
    path: '/settings/feedback-questions',
    Component: lazy(() => import('pages/survey')),
    exact: true,
  },
  {
    path: '/customer-feedback',
    Component: lazy(() => import('pages/survey/feedback/index')),
    exact: true,
  },

  // OAuth
  {
    path: '/oauth/dialog',
    Component: lazy(() => import('pages/oauth/dialog')),
    exact: true,
  },

  // Billing
  {
    path: '/billing/overview',
    Component: lazy(() => import('pages/billing/overview')),
    exact: true,
  },
  {
    path: '/billing/invoices',
    Component: lazy(() => import('pages/billing/invoices')),
    exact: true,
  },

  // inbox
  {
    path: '/inbox',
    Component: lazy(() => import('pages/inbox')),
    exact: true,
  },

  // digital signage
  {
    path: '/digital-signage',
    Component: lazy(() => import('pages/digital-signage')),
    exact: true,
  },

  // users
  {
    path: '/users',
    Component: lazy(() => import('pages/users')),
    exact: true,
  },
]

const FallBack = () => {
  useEffect(() => {
    nProgress.start()
    return () => {
      nProgress.done()
    }
  }, [])

  return null
}

const mapStateToProps = ({ settings }) => ({
  routerAnimation: settings.routerAnimation,
})

const Router = ({ history, routerAnimation }) => {
  return (
    <ConnectedRouter history={history}>
      <Layout>
        <Route
          render={state => {
            const { location } = state
            return (
              <SwitchTransition>
                <CSSTransition
                  key={location.pathname}
                  appear
                  classNames={routerAnimation}
                  timeout={routerAnimation === 'none' ? 0 : 200}
                >
                  <Switch location={location}>
                    <Route exact path="/">
                      <Landing />
                    </Route>
                    {routes.map(({ path, Component, exact }) => (
                      <Route
                        path={path}
                        key={path}
                        exact={exact}
                        render={() => {
                          return (
                            <div
                              className={`${routerAnimation}${
                                path.includes('/inbox') ? ' router-container' : ''
                              }`}
                            >
                              <Suspense fallback={<FallBack />}>
                                <Component />
                              </Suspense>
                            </div>
                          )
                        }}
                      />
                    ))}
                    <Route path="*">
                      <Landing />
                    </Route>
                  </Switch>
                </CSSTransition>
              </SwitchTransition>
            )
          }}
        />
      </Layout>
    </ConnectedRouter>
  )
}

export default connect(mapStateToProps)(Router)
